import React from 'react';
import { Button, CircularProgress } from '@mui/material';
import ExportCsvIcon from '../../assets/icons/Exportcsvicon.png';
import apiService from '../../services/api';
import * as XLSX from 'xlsx';
import { useNotification } from '../../contexts/NotificationContext';

interface CsvExportProps {
  apiEndpoint: string;
  apiPayload?: any | (() => any);
  filename?: string;
  buttonLabel?: string;
  disabled?: boolean;
  method?: 'get' | 'post';
  dataTransformer?: (data: any[]) => any[];
  onExportStart?: () => void;
  onExportComplete?: () => void;
  onExportError?: (error: string) => void;
  customApiCall?: (payload: any) => Promise<any>;
}

const CsvExport: React.FC<CsvExportProps> = ({
  apiEndpoint,
  apiPayload = {},
  filename = 'export.csv',
  buttonLabel = 'Export CSV',
  disabled = false,
  method = 'get',
  dataTransformer,
  onExportStart,
  onExportComplete,
  onExportError,
  customApiCall,
}) => {
  const [exporting, setExporting] = React.useState(false);
  const { showNotification } = useNotification();

  const getValueTextForWidth = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (value instanceof Date) return value.toISOString();
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  };

  const getExcelColumnWidths = (rows: any[], maxWch: number = 50, minWch: number = 10) => {
    if (!rows || rows.length === 0) return [];
    const headers = Object.keys(rows[0] || {});
    return headers.map((header) => {
      let maxLen = getValueTextForWidth(header).length;
      for (const row of rows) {
        const cellText = getValueTextForWidth(row?.[header]);
        maxLen = Math.max(maxLen, cellText.length);
      }
      const wch = Math.min(maxWch, Math.max(minWch, maxLen + 2));
      return { wch };
    });
  };

  const isRecord = (value: unknown): value is Record<string, any> =>
    typeof value === 'object' && value !== null;

  const getPayloadFromResponse = (response: any) => {
    if (isRecord(response) && 'data' in response && 'status' in response) return response.data;
    return response;
  };

  const notifyNoData = (payload: Record<string, any>) => {
    const msg =
      typeof payload.message === 'string' && payload.message.trim().length > 0
        ? payload.message
        : 'Records not found';
    const statusCode = payload.statusCode;
    const isSuccess = payload.isSuccess;

    if (statusCode === 200 && isSuccess === true) {
      showNotification(msg, 'warning');
      onExportError?.(msg);
      return;
    }

    showNotification(msg, isSuccess === false ? 'error' : 'warning');
    onExportError?.(msg);
  };

  const extractArrayDataFromResponse = (response: any): any[] | null | undefined => {
    const payload = getPayloadFromResponse(response);

    if (Array.isArray(payload)) return payload;

    if (isRecord(payload) && 'data' in payload) {
      const envelopeData = payload.data;
      if (
        isRecord(envelopeData) &&
        'items' in envelopeData &&
        Array.isArray((envelopeData as { items: unknown }).items)
      ) {
        const items = (envelopeData as { items: unknown[] }).items;
        if (items.length === 0) {
          notifyNoData(payload);
          return null;
        }
        return items;
      }
      const hasNoData =
        envelopeData === null ||
        envelopeData === undefined ||
        (Array.isArray(envelopeData) && envelopeData.length === 0);

      if (hasNoData) {
        notifyNoData(payload);
        return null;
      }

      if (Array.isArray(envelopeData)) return envelopeData;
    }

    return undefined;
  };

  const convertToCSV = (data: any[]): string => {
    if (!data || data.length === 0) return '';
    const headers = Object.keys(data[0]);    

    const csvHeaders = headers.join(',');
    
    const csvRows = data.map(row => {
      return headers.map(header => {
        const value = row[header];
        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
          return `"${value.replaceAll('"', '""')}"`;
        }
        return value || '';
      }).join(',');
    });
    
    return [csvHeaders, ...csvRows].join('\n');
  };

  const downloadCSV = (csvContent: string, filename: string) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  };

  const downloadExcel = (data: any[], filename: string) => {
    if (!data || data.length === 0) {
      throw new Error('No data available to export');
    }

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(data);
    
    worksheet['!cols'] = getExcelColumnWidths(data, 50, 10);
    
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    
    XLSX.writeFile(workbook, filename);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      onExportStart?.();

      const resolvedPayload = typeof apiPayload === 'function' ? apiPayload() : apiPayload;

      let response: any;
      if (customApiCall) {
        response = await customApiCall(resolvedPayload);
      } else if (method === 'post') {
        response = await apiService.post(apiEndpoint, resolvedPayload);
      } else {
        const config = resolvedPayload ? { params: resolvedPayload } : undefined as any;
        response = await apiService.get(apiEndpoint, config);
      }
      const extracted = extractArrayDataFromResponse(response);
      if (extracted === null) {
        return;
      }
      if (extracted === undefined) {
        throw new Error('Invalid data format received from API');
      }
      let data = extracted;

      if (dataTransformer) {
        data = dataTransformer(data);
      }

      const isExcelExport = filename.toLowerCase().endsWith('.xlsx');
      
      if (isExcelExport) {
        downloadExcel(data, filename);
      } else {
        const csvContent = convertToCSV(data);
        
        if (!csvContent) {
          throw new Error('No data available to export');
        }

        downloadCSV(csvContent, filename);
      }
      
      onExportComplete?.();
    } catch (error: any) {
      console.error('CSV Export Error:', error);
      const errorMessage = error.message || 'Failed to export CSV';
      if (!onExportError) {
        showNotification(errorMessage, 'error');
      }
      onExportError?.(errorMessage);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Button
      variant="contained"
      className="px-4 py-2 fw-medium text-white ms-2 d-flex align-items-center"
      onClick={handleExport}
      disabled={disabled || exporting}
      sx={{
        backgroundColor: "rgba(34, 197, 94, 1)",
        "&:hover": { 
          backgroundColor: "rgba(34, 197, 94, 0.9)",
        },
        "&:disabled": {
          backgroundColor: "rgba(34, 197, 94, 0.5)",
        },
      }}
    >
      {exporting ? (
        <CircularProgress size={16} color="inherit" className="me-2" />
      ) : (
        <img src={ExportCsvIcon} alt="Export CSV" width="16" height="16" className="me-2" />
      )}
      {exporting ? 'Exporting...' : buttonLabel}
    </Button>
  );
};

export default CsvExport;
